import { Namespace, TranslationKey, useT } from "@racoon/i18n";
import {
  IcDashboard,
  IcTask,
  IcRoutes,
  IcGroupOfLocations,
  IcCar,
  IcCompanies,
  IcLocation,
  IcCalendar,
  IcGarrage,
  IcAnalytics,
  IcUsers,
  IcExcel,
  IcNavigation,
  IcWeightStation,
  IcBunker,
  LogoRaccoonAi as Logo,
  LogoSmall,
} from "@racoon/icons";
import {
  Button,
  Sheet,
  SheetContent,
  SheetTrigger,
  useCompanyBase,
  NavItem,
  SidebarContent,
  SidebarContentProps,
  NotificationPanel,
  SIDEBAR_GRID_EXPANDED,
  SIDEBAR_GRID_COLLAPSED,
  cn,
} from "@racoon/ui";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import { Menu } from "lucide-react";
import { useState, useEffect } from "react";
import { Outlet, useLocation, useParams, useNavigate } from "react-router-dom";
import { useAssignRouteModal } from "../../features/assign-route/hooks/use-assign-route-modal";
import { useCreateOrderDialog } from "../../pages/dashboard/orders/ui/use-create-order.dialog";
import { useNotifications } from "../../shared/hooks/use-notifications";
import { InfoPanel } from "~/features/info-panel/ui/info.panel";
import { useGetServiceProviderStatsQuery } from "~/shared/api/analytics/analytics.grpc";
import { useServiceProviderStats } from "~/shared/store/stats/hooks/useServiceProviderStats";

import {
  useGetOrganizationSettingsQuery,
  useUpdateOrganizationSettingsMutation,
} from "~/shared/api/organization/organization.grpc";
import { useMediaQuery } from "~/shared/hooks/useMediaQuery";
import { useSidebarCollapsed } from "~/shared/store/ui";

export default function PrivateLayout() {
  const t = useT();
  const navigate = useNavigate();
  const { user, isReceiverUser } = useAuthSession();
  const isMd = useMediaQuery("(min-width: 768px)");
  const { isSidebarCollapsed, setCollapsed } = useSidebarCollapsed();

  const [open, setOpen] = useState(false);
  const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);
  const { openCreateOrder } = useCreateOrderDialog();
  const { openAssignRoute } = useAssignRouteModal();

  const base = useCompanyBase();
  const { pathname } = useLocation();
  const { companyId } = useParams();

  // Fetch stats with polling and save to global store
  const { data: serviceProviderStatsData } = useGetServiceProviderStatsQuery(
    { ownerOrganizationId: BigInt(Number(companyId) || 0) },
    {
      skip: !companyId,
      pollingInterval: 5 * 60 * 1000,
    },
  );

  const { stats: serviceProviderStats, setStats } = useServiceProviderStats();

  useEffect(() => {
    if (serviceProviderStatsData) {
      setStats(serviceProviderStatsData);
    }
  }, [serviceProviderStatsData, setStats]);

  // ==========================================
  // LOGIC FOR AI SWITCH BUTTON
  // ==========================================

  // 1. Get organization settings (own org, resolved from auth context)
  const { data: organizationData } = useGetOrganizationSettingsQuery({}, { skip: !companyId });

  // 2. Initialize update mutation
  const [updateSettings, { isLoading: isSettingsUpdating }] =
    useUpdateOrganizationSettingsMutation();

  // 3. Toggle function
  const handleAiToggle = async (checked: boolean) => {
    if (!organizationData) return;
    try {
      await updateSettings({
        id: organizationData.id,
        isAiDriven: checked,
      }).unwrap();
    } catch (error) {
      console.error("Failed to update AI settings:", error);
    }
  };
  // ==========================================

  const {
    logs,
    authors,
    isLoading: isLoadingNotifications,
    formatTime,
    formatDate,
    totalCount,
    loadNextPage: loadNextNotifications,
    refetch: refetchNotifications,
  } = useNotifications();

  const handleAssignRoute = () => {
    openAssignRoute();
  };

  const handleCreateOrder = () => {
    openCreateOrder();
  };

  const handleNotificationPanelOpen = () => {
    setIsNotificationPanelOpen(true);
  };

  const handleSelectCompany = () => {
    navigate("/organization-admin");
  };

  const handleChangePassword = () => {
    navigate("/change-password");
  };

  // ... NAVIGATION MENU ...
  const adminNavItems: NavItem[] = [
    {
      to: "/organization-admin",
      key: "organizations",
      icon: IcCompanies as any,
      label: "Организации",
    },
    {
      to: "/platform-users",
      key: "platform-users",
      icon: IcUsers as any,
      label: "Пользователи",
    },
  ];

  const receiverNavItems: NavItem[] = [
    {
      to: `${base}/orders`,
      key: "orders",
      icon: IcTask as any,
      label: t(Namespace.Sidebar, "orders"),
    },
    {
      to: `${base}/dashboard`,
      key: "dashboard",
      icon: IcDashboard as any,
      label: t(Namespace.Sidebar, "dashboard"),
    },
    {
      to: `${base}/pickup-locations`,
      key: "objects",
      icon: IcLocation as any,
      label: t(Namespace.Sidebar, "objects"),
    },
    {
      to: `${base}/service-providers`,
      key: "serviceProviders",
      icon: IcCompanies as any,
      label: "Поставщики Услуг",
    },
    {
      to: `${base}/contracts`,
      key: "contracts",
      icon: IcExcel as any,
      label: "Договора",
    },
  ];

  const dispatcher: NavItem[] = [
    {
      to: `${base}/dashboard`,
      key: "dashboard",
      icon: IcDashboard as any,
      label: t(Namespace.Sidebar, "dashboard"),
    },
    {
      to: `${base}/orders`,
      key: "orders",
      icon: IcTask as any,
      label: t(Namespace.Sidebar, "orders"),
      count: serviceProviderStats?.unassignedJobCount,
      countTooltipText: t(Namespace.Sidebar, "countTooltip.orders"),
    },
    {
      to: `${base}/routes`,
      key: "routes",
      icon: IcRoutes as any,
      label: t(Namespace.Sidebar, "routes"),
      count: serviceProviderStats?.activeRouteCount,
      countTooltipText: t(Namespace.Sidebar, "countTooltip.routes"),
    },
    {
      to: `${base}/pickup-location-groups`,
      key: "routeGroups",
      icon: IcGroupOfLocations as any,
      label: t(Namespace.Sidebar, "routeGroups"),
    },
    {
      to: `${base}/pickup-locations`,
      key: "objects",
      icon: IcLocation as any,
      label: t(Namespace.Sidebar, "objects"),
    },
    {
      to: `${base}/customers`,
      key: "customers",
      icon: IcCompanies as any,
      label: t(Namespace.Sidebar, "customers"),
    },
    {
      to: `${base}/gps-tracking`,
      key: "gpsTracking",
      icon: IcNavigation as any,
      label: t(Namespace.Sidebar, "gpsTracking"),
      disabled: !isMd,
    },
    {
      to: `${base}/weight-management`,
      key: "weightManagement",
      icon: IcWeightStation as any,
      label: t(Namespace.Sidebar, "weight"),
    },
    {
      to: `${base}/inventory`,
      key: "inventory",
      icon: IcBunker,
      label: t(Namespace.Sidebar, "inventory"),
    },
  ];

  const mechanic: NavItem[] = [
    {
      to: `${base}/autopark`,
      key: "autopark",
      icon: IcGarrage as any,
      label: t(Namespace.Sidebar, "autopark"),
    },
    {
      to: `${base}/shift-schedule`,
      key: "shiftSchedule",
      icon: IcCalendar as any,
      label: t(Namespace.Sidebar, "shiftSchedule"),
    },
  ];

  const manager: NavItem[] = [
    {
      to: `${base}/analytics`,
      key: "analytics",
      icon: IcAnalytics as any,
      label: t(Namespace.Sidebar, "analytics"),
    },
    {
      to: `${base}/employees`,
      key: "employees",
      icon: IcUsers as any,
      label: t(Namespace.Sidebar, "employees"),
    },
  ];

  const sidebarItems: SidebarContentProps["sidebarItems"] = !companyId
    ? [
        {
          routes: adminNavItems,
          title: "Супер Администратор",
          icon: undefined,
          defaultOpen: true,
        },
      ]
    : isReceiverUser
      ? [
          {
            routes: receiverNavItems,
            title: "Receiver",
            icon: undefined,
            defaultOpen: true,
          },
        ]
      : [
          {
            routes: dispatcher,
            title: t(Namespace.Sidebar, "groups.dispatcher"),
            icon: undefined,
            defaultOpen: true,
          },
          {
            routes: mechanic,
            title: t(Namespace.Sidebar, "groups.mechanic"),
            icon: undefined,
            defaultOpen: true,
          },
          {
            routes: manager,
            title: t(Namespace.Sidebar, "groups.manager"),
            icon: undefined,
            defaultOpen: true,
          },
        ];

  const ROUTE_KEY_FALLBACK = "dashboard";
  const routeKeyMap: Record<string, string> = {
    dashboard: "dashboard",
    orders: "orders",
    routes: "routes",
    "pickup-location-groups": "routeGroups",
    trucks: "trucks",
    "pickup-locations": "pickupLocations",
    customers: "customers",
    employees: "employees",
    autopark: "autopark",
    "shift-schedule": "shiftSchedule",
    analytics: "analytics",
    "select-company": "selectCompany",
    "service-providers": "serviceProviders",
    contracts: "contracts",
    "organization-admin": "organizations",
    "platform-users": "platformUsers",
    "gps-tracking": "gpsTracking",
    "weight-management": "weightManagement",
    inventory: "inventory",
  };

  const currentRouteKey = (() => {
    if (companyId) {
      const seg = pathname.split("?")[0].split("/")[2] || ROUTE_KEY_FALLBACK;
      return routeKeyMap[seg] ?? ROUTE_KEY_FALLBACK;
    }
    const seg = pathname.split("?")[0].split("/")[1] || ROUTE_KEY_FALLBACK;
    return routeKeyMap[seg] ?? ROUTE_KEY_FALLBACK;
  })();

  const pageTitle = t(Namespace.Routes, currentRouteKey as TranslationKey) || currentRouteKey;

  const getCurrentRouteIcon = () => {
    const iconMap: Record<string, any> = {
      dashboard: IcDashboard,
      orders: IcTask,
      routes: IcRoutes,
      routeGroups: IcGroupOfLocations,
      trucks: IcCar,
      pickupLocations: IcLocation,
      customers: IcCompanies,
      employees: IcUsers,
      autopark: IcGarrage,
      shiftSchedule: IcCalendar,
      analytics: IcAnalytics,
      serviceProviders: IcCompanies,
      contracts: IcExcel,
      organizations: IcCompanies,
      platformUsers: IcUsers,
      gpsTracking: IcNavigation,
      weightManagement: IcWeightStation,
      inventory: IcBunker,
    };
    return iconMap[currentRouteKey] || IcDashboard;
  };

  const CurrentRouteIcon = getCurrentRouteIcon();

  // Both call sites below render the same sidebar; only the collapsed state
  // differs. Inside the mobile Sheet the drawer *is* the disclosure, so the
  // rail would be redundant — it always renders expanded there.
  const sharedSidebarProps: SidebarContentProps = {
    sidebarItems,
    title: "",
    user: user as any,
    isWeightControl: false,
    isSwitchVisible: !!companyId && (organizationData?.aiEnabled ?? false),
    isAiDrivenValue: organizationData?.isAiDriven ?? false,
    onAiChange: handleAiToggle,
    isAiLoading: isSettingsUpdating,
    onCreateBtn: companyId ? handleCreateOrder : undefined,
    ...(!companyId || isReceiverUser ? {} : { onAssignBtn: handleAssignRoute }),
    onNotificationClick: handleNotificationPanelOpen,
    onChangePassword: handleChangePassword,
    notificationCount: totalCount,
    version: import.meta.env.VITE_APP_VERSION,
    onSelectCompany: handleSelectCompany,
    collapsedLogo: <LogoSmall />,
  };

  return (
    <div className="min-h-dvh w-full bg-slate-50 dark:bg-slate-950 !bg-text-invert">
      <header className="lg:hidden sticky top-0 z-40 h-14 border-b bg-white/80 backdrop-blur dark:bg-slate-900/70">
        <div className="h-full px-4 flex items-center gap-3">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                id="private-layout-menu-button"
                variant="ghost"
                size="icon"
                aria-label={t(Namespace.Accessibility, "openMenu")}
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-auto">
              <div className="h-dvh">
                <SidebarContent
                  {...sharedSidebarProps}
                  isCollapsed={false}
                  logo={<Logo className="h-12 w-auto shrink-0" />}
                />
              </div>
            </SheetContent>
          </Sheet>

          <div className="flex items-center gap-2">
            <CurrentRouteIcon className="h-6 w-6" />
            <div className="font-semibold truncate">{pageTitle}</div>
          </div>
          <div className="ml-auto flex items-center gap-2" />
        </div>
      </header>

      <div className="px-4 h-[100vh]">
        <div
          className={cn(
            "lg:grid lg:gap-4 py-4 transition-[grid-template-columns] duration-300 ease-in-out",
            isSidebarCollapsed ? SIDEBAR_GRID_COLLAPSED : SIDEBAR_GRID_EXPANDED,
          )}
        >
          <aside className="hidden lg:block">
            <div className="sticky top-0 bg-white dark:bg-slate-900 rounded-xl border overflow-hidden">
              <div className="h-[calc(100dvh-2rem-2px)]">
                <SidebarContent
                  {...sharedSidebarProps}
                  isCollapsed={isSidebarCollapsed}
                  onCollapsedChange={setCollapsed}
                  logo={<Logo className="h-12 w-36 shrink-0" />}
                />
              </div>
            </div>
          </aside>

          <main className="min-w-0 overflow-y-scroll h-[calc(100dvh-2rem)] flex overflow-x-hidden">
            <div className="flex flex-1 w-full min-w-0">
              <Outlet />
            </div>
            {companyId && <InfoPanel />}
          </main>
        </div>
      </div>

      <NotificationPanel
        items={logs}
        isLoading={isLoadingNotifications}
        authors={authors}
        timeFormatter={formatTime}
        dateFormatter={formatDate}
        open={isNotificationPanelOpen}
        onOpenChange={setIsNotificationPanelOpen}
        onOpen={refetchNotifications}
        onEndReached={loadNextNotifications}
        totalCount={totalCount}
        tabs={[
          { key: "all", label: t(Namespace.AuditLogs, "tabs.all") },
          { key: "AUDIT_LOG_CATEGORY_TRUCK", label: t(Namespace.AuditLogs, "category.trucks") },
          { key: "AUDIT_LOG_CATEGORY_OFFICE", label: t(Namespace.AuditLogs, "category.office") },
          {
            key: "AUDIT_LOG_CATEGORY_AI_DECISION",
            label: t(Namespace.AuditLogs, "category.aiDispatcher"),
          },
        ]}
      />
    </div>
  );
}
